import React, { useCallback, useEffect, useState } from 'react';
import { UsersRound, Plus, Trash2, KeyRound, Crown, Loader2, X, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { adminListUsers, adminCreateUser, adminDeleteUser, adminResetUserPassword } from '../../api';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const UsersCard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newForm, setNewForm] = useState({ username: '', password: '' });
  const [creating, setCreating] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    try { setUsers(await adminListUsers()); }
    catch (err) { console.warn('load users failed', err?.response?.data?.detail || err?.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!newForm.username.trim() || !newForm.password.trim()) return;
    setCreating(true);
    try {
      await adminCreateUser({ username: newForm.username.trim(), password: newForm.password, role: 'admin' });
      toast({ title: 'User created', description: `${newForm.username} can now sign in.` });
      setNewForm({ username: '', password: '' });
      await load();
    } catch (err) {
      toast({ title: 'Could not create user', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"? They will lose access immediately.`)) return;
    try {
      await adminDeleteUser(u.id);
      toast({ title: 'User deleted' });
      await load();
    } catch (err) {
      toast({ title: 'Could not delete', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const submitReset = async () => {
    if (!resetTarget || resetPwd.length < 8) {
      toast({ title: 'Password too short', description: 'Minimum 8 characters.', variant: 'destructive' });
      return;
    }
    setResetting(true);
    try {
      await adminResetUserPassword(resetTarget.id, resetPwd);
      toast({ title: 'Password reset', description: `Share the new password with ${resetTarget.username}.` });
      setResetTarget(null);
      setResetPwd('');
    } catch (err) {
      toast({ title: 'Reset failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setResetting(false); }
  };

  return (
    <div className="rounded-xl border border-cyan-400/10 bg-slate-900/30 p-7">
      <div className="flex items-center gap-3 mb-2">
        <UsersRound className="w-5 h-5 text-cyan-400" />
        <h3 className="text-xl font-semibold text-white">Admin Users</h3>
      </div>
      <p className="text-sm text-slate-400 mb-5">Create additional admins who can access this dashboard. Only owners can manage users.</p>

      {loading ? (
        <div className="text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading...</div>
      ) : (
        <div className="space-y-2 mb-5">
          {users.map(u => (
            <div key={u.id} className="flex items-center justify-between gap-3 rounded-md border border-cyan-400/10 bg-[#080d18] px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                {u.role === 'owner' ? <Crown className="w-4 h-4 text-amber-400 flex-shrink-0" /> : <ShieldCheck className="w-4 h-4 text-cyan-400 flex-shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{u.username} {u.username === user?.username && <span className="text-slate-500 text-xs">(you)</span>}</p>
                  <p className="text-xs text-slate-500 capitalize">{u.role} &middot; created {new Date(u.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              {u.role !== 'owner' && (
                <div className="flex items-center gap-2">
                  <Button onClick={() => setResetTarget(u)} variant="outline" className="h-8 border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200" title="Reset password">
                    <KeyRound className="w-3.5 h-3.5" />
                  </Button>
                  <Button onClick={() => remove(u)} variant="outline" className="h-8 border-red-900/40 bg-transparent hover:bg-red-950/40 text-red-300" title="Delete user">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={create} className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-slate-300 text-xs mb-1.5 block">New Username</Label>
          <Input required value={newForm.username} onChange={e => setNewForm({ ...newForm, username: e.target.value })} placeholder="username" minLength={3} maxLength={64} autoComplete="off" className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" />
        </div>
        <div>
          <Label className="text-slate-300 text-xs mb-1.5 block">Initial Password (min 8)</Label>
          <Input required type="text" value={newForm.password} onChange={e => setNewForm({ ...newForm, password: e.target.value })} placeholder="temporary password" minLength={8} maxLength={128} autoComplete="off" className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white font-mono" />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={creating} className="bg-cyan-400 hover:bg-cyan-300 disabled:opacity-70 text-slate-900 font-semibold rounded-md h-10 px-4">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create Admin User</>}
          </Button>
          <p className="text-xs text-slate-500 mt-2">Share the username and initial password with the new admin. They can change their password from Settings after first sign in.</p>
        </div>
      </form>

      <Dialog open={!!resetTarget} onOpenChange={v => { if (!v) { setResetTarget(null); setResetPwd(''); } }}>
        <DialogContent className="bg-[#0d1424] border border-cyan-400/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Reset password</DialogTitle>
            <DialogDescription className="text-slate-400">Set a new password for <span className="text-slate-200 font-medium">{resetTarget?.username}</span>. Share it with them privately.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-slate-300 text-xs mb-1.5 block">New Password (min 8 chars)</Label>
            <Input type="text" value={resetPwd} onChange={e => setResetPwd(e.target.value)} minLength={8} maxLength={128} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white font-mono" />
          </div>
          <DialogFooter>
            <Button onClick={() => { setResetTarget(null); setResetPwd(''); }} variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={submitReset} disabled={resetting} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold">{resetting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : 'Reset Password'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersCard;
